// Import Packages
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Import Material UI
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { Autocomplete } from '@material-ui/lab';
import { DialogContentText, TextField } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';

// Import Pages
import { TextInput } from '../FlowConfigurationInputs/TextInput/NewTextInput';
import FieldLabel from '../FlowConfigurationInputs/FieldLabel/FieldLabel';
import { Checkboxes } from '../FlowConfigurationInputs';

// Import Recoil
import { alertState } from '../../recoil-store/recoil-state';

// Import Constants
import { UNIQUE_SEPERATOR } from '../../helpers/constant'

// Import CSS
import './MathFunc.css';
import { useSetRecoilState } from 'recoil';

export default function MathFunc({ setOpen, hideInput, text, addChip, variables, setOpenVariable, editFuncName, selectedFunction }) {
    const { t } = useTranslation();
    const [optionValue, setOptionValue] = useState("")
    const [countryCode, setCountryCode] = useState(false)
    const [checkAreaCode, setCheckAreaCode] = useState(false)
    const [phoneFormat, setPhoneFormat] = useState({
        areaCode: "",
        phoneNumber: ""
    })
    const setAlert = useSetRecoilState(alertState);

    useEffect(() => {
        if (editFuncName) {
            let isCountryCode
            let isAreaCode
            selectedFunction.fields?.forEach((item, index) => {
                if (item.options) {
                    setOptionValue(item.value)
                }
                if (item.name === "country_code" && item.value) {
                    isCountryCode = true
                    setCountryCode(true)
                }
                else if (item.name === "area_code" && item.value) {
                    isAreaCode = true
                    setCheckAreaCode(true)
                }
                else if (item.type === "phoneFormat" && item.value) {
                    const parts = item.value.split(' ');
                    const remaining = parts.slice(isCountryCode && isAreaCode ? 2 : isAreaCode || isAreaCode ? 1 : 0)
                    const phoneNumber = remaining.join(' ');
                    if (parts.length) {
                        setPhoneFormat({
                            areaCode: isAreaCode ? parts[isCountryCode ? 1 : 0] : "",
                            phoneNumber
                        })
                    }
                }
            })
        }
    }, [])

    const handleClose = () => {
        setOpen(undefined)
        hideInput()
    }

    function generateIdentifier() {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        const charactersLength = characters.length;
        for (let i = 0; i < 6; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    const handleSubmit = () => {
        let textSecondHalf = ""
        let isOk = true
        const variableRegex = /(##[\w-]+(\[[-\w\s]+\])*##)/g;
        selectedFunction.fields?.forEach((item, index) => {
            let value = item.value
            variableRegex.lastIndex = 0;
            // Değişken tanımlı regex imiz fonksiyon değerleri içinde bulunuyorsa yanında başka bir değer yoksa kabul ediyoruz.
            // Yanında başka bir değer varsa tipine göre kontrol ediyoruz.
            const variableArr = value && value !== true ? value.match(variableRegex) : undefined;
            if (variableArr) {
                let cloneValue = value;
                for (let v of variableArr) {
                    cloneValue = cloneValue.replace(v, '')
                }
                if (cloneValue) {
                    if (item.type === "number" && isNaN(cloneValue)) {
                        isOk = false
                        return setAlert(alert => ({
                            ...alert,
                            show: true,
                            message: t(item.label) + " " + t("CONTENT.FUNCTIONS.TYPE-ERROR") + " " + item.type,
                            severity: 'error'
                        }))
                    }
                }
            } else {
                // Zorunlu alanlar boşsa hata veriyoruz.
                if (item.isRequired) {
                    if (!value) {
                        isOk = false
                        return setAlert(alert => ({
                            ...alert,
                            show: true,
                            message: t("GENERAL.NOT-NULL"),
                            severity: 'error'
                        }))
                    }
                }
                // Değer tipine göre kontrol yapıyoruz.
                if (value && item.type === "number" && isNaN(value)) {
                    isOk = false
                    return setAlert(alert => ({
                        ...alert,
                        show: true,
                        message: t(item.label) + " " + t("CONTENT.FUNCTIONS.TYPE-ERROR") + " " + item.type,
                        severity: 'error'
                    }))
                }
                if (value && item.type === "checkbox") {
                    if (item.checkbox) {
                        value = item.checkbox[value ? 1 : 0].replaced
                    }
                }
                if (item.type === "phoneFormat") {
                    if ((checkAreaCode && !phoneFormat.areaCode && phoneFormat.phoneNumber) || (checkAreaCode && phoneFormat.areaCode && !phoneFormat.phoneNumber)) {
                        isOk = false
                        return setAlert(alert => ({
                            ...alert,
                            show: true,
                            message: t("CONTENT.FUNCTIONS.FORMAT-ERROR"),
                            severity: 'error'
                        }))
                    }
                    value = `${countryCode ? "* " : ""}${phoneFormat.areaCode && checkAreaCode ? phoneFormat.areaCode + " " : ""}${phoneFormat.phoneNumber ? phoneFormat.phoneNumber : ""}`
                }
            }

            // Değer içerisinde format: key i varsa, valuesi içerisindeki '%' ile asıl değerimizi değiştiriyoruz. 
            if (item.format && value) {
                value = item.format.replace(/%/g, value);
            }
            if (item.value || (item.type === "phoneFormat" && value)) {
                textSecondHalf += `${index > 0 ? UNIQUE_SEPERATOR : ""}${value}`
            }



        })
        if (!isOk) return
        let completedText = "";
        let newText = "";
        textSecondHalf = textSecondHalf.replace(/\n/g, '');
        if (editFuncName) {
            // Fonksiyon editlenirken input içerisindeki eski fonksiyon değerlerine yenisini basıyoruz ve identifier değiştirmiyoruz.
            completedText = `fx.${selectedFunction.functionName}(${textSecondHalf}${UNIQUE_SEPERATOR}${selectedFunction.identifier})`
            newText = text.replace(editFuncName, completedText);
        } else {
            // Fonksiyon oluşturulurken identifier ekliyoruz.
            completedText = `fx.${selectedFunction.functionName}(${textSecondHalf}${UNIQUE_SEPERATOR}${generateIdentifier()})`
            newText = text.replace(/fn\./g, completedText)
        }
        addChip(newText, completedText)
        setOpen(undefined)
        hideInput()
    }

    const handleChange = (value, index) => {
        selectedFunction.fields[index] = { ...selectedFunction.fields[index], value: value }
        setOptionValue(value)
    }

    const handlePhoneformatChange = (value, from) => {
        if (from === "areaCode") {
            if (/^[*]*$/.test(value)) {
                setPhoneFormat((pre) => {
                    return { ...pre, areaCode: value }
                })
            }
        }
        else if (from === "phoneNumber") {
            if (/^[ *]*$/.test(value)) {
                setPhoneFormat((pre) => {
                    return { ...pre, phoneNumber: value }
                })
            }
        }
    }

    const handleCheck = (value, index) => {
        if (selectedFunction.fields[index] && selectedFunction.fields[index].name === "country_code") {
            setCountryCode(value)
        }
        else if (selectedFunction.fields[index] && selectedFunction.fields[index].name === "area_code") {
            setCheckAreaCode(value)
        }
    }


    const inputSelect = (field, index) => {
        switch (field.type) {
            case "number":
            case "string":
            case "array":
                return <TextInput action={selectedFunction} inputName={index} variables={variables} setOpenVariable={setOpenVariable} outOnChange={(e) => handleCheck(e, index)}
                    isRequired={field.isRequired} multiline={false} fieldType={field.type} defaultValue={field.defaultValue} hideRequired />
            case "select":
                return <Autocomplete
                    onChange={(event, newValue) => {
                        handleChange(newValue?.value ? newValue?.value : "", index)
                    }}
                    value={optionValue}
                    size="small"
                    options={field.options.length > 0 ? field.options : []}
                    getOptionLabel={(item) => {
                        if (item.value) {
                            return item.value
                        }
                        return item
                    }}
                    renderInput={(params) => <TextField {...params} variant="outlined" />}
                />
                break
            case "checkbox":
                return <Checkboxes
                    action={selectedFunction}
                    inputName={index}
                    onChange={(e) => handleCheck(e.value, index)}
                    options={{ label: field.label }}
                    isRequired={field.isRequired} />
                break
            case "phoneFormat":
                return <div className={`phone-format-input-container`}>
                    {countryCode && <TextField
                        className="phone-input"
                        value={"*"}
                        id="standard"
                        label="C Code"
                        disabled
                        variant="outlined"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        style={{ maxWidth: "70px" }}
                    />}
                    {checkAreaCode && <TextField
                        className="phone-input"
                        id="standard"
                        label="A Code"
                        variant="outlined"
                        value={phoneFormat.areaCode}
                        onChange={(e) => handlePhoneformatChange(e.target.value, "areaCode")}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">{"("}</InputAdornment>,
                            endAdornment: <InputAdornment position="end">{")"}</InputAdornment>
                        }}
                        style={{ maxWidth: "120px" }}
                    />}
                    <TextField
                        className="phone-input"
                        id="standard"
                        label="Number"
                        autoComplete="current-password"
                        variant="outlined"
                        value={phoneFormat.phoneNumber}
                        onChange={(e) => handlePhoneformatChange(e.target.value, "phoneNumber")}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        style={{ flex: "auto" }}
                    />
                </div>
                break
            default:
                return <TextInput action={selectedFunction} inputName={index} variables={variables} setOpenVariable={setOpenVariable} outOnChange={(e) => handleCheck(e.value, index)}
                    isRequired={field.isRequired} multiline={false} fieldType={field.type} defaultValue={field.defaultValue} hideRequired />
                break;
        }
    }

    return (
        <Dialog onClose={handleClose} open={true} PaperProps={{ style: { overflowY: "visible", minWidth: "30%" } }}>
            <DialogTitle>
                {t(selectedFunction.label)}
            </DialogTitle>
            <DialogContent style={{ minWidth: 300, overflowY: "none" }}>
                {selectedFunction.fields?.map((field, index) => {
                    return <DialogContentText>
                        {field.type !== "checkbox" && <FieldLabel name={t(field.label)} is_required={field.isRequired} />}
                        {inputSelect(field, index)}
                        {field.description && <small style={{ fontSize: "0.7rem" }} className='conf-field-desc'>{t(field.description)}</small>}
                    </DialogContentText>
                })}
            </DialogContent>
            <DialogActions className="modal-actions">
                <DialogContentText>
                    <Button
                        data-test="submitdialog-button"
                        onClick={handleSubmit}
                        variant="outlined"
                        style={{
                            float: "right",
                            backgroundColor: "#46474E",
                            borderRadius: "2px",
                            color: "#fff",
                            width: "40%",
                            border: "none"
                        }}>
                        {t("CONTENT.ROLE.SAVE")}
                    </Button>
                </DialogContentText>
            </DialogActions>
        </Dialog>
    )
};
